/**
 * Style GENERATION guard for the base-style teardown/rebuild.
 *
 * map.setStyle() throws the whole style away and builds a new one. MapLibre's
 * addSource/addLayer/setLayoutProperty/setPaintProperty/setFilter/setGlyphs all
 * call Style._checkLoaded() first, which throws "Style is not done loading." until
 * the NEW style has parsed (Style._loaded flips back to true). So any overlay
 * re-apply that mutates the map has to run against a genuinely-loaded style.
 *
 * isStyleReady() covers the single-shot case, but it is a POINT-IN-TIME read. Our
 * re-apply work is asynchronous: the palette re-apply and the initial mount both
 * `await trainingChartSource.loadCell(...)` and only THEN call addSource/addLayer.
 * A gate checked once at the top has gone stale by the time the post-await
 * mutations run: if a second chart/style change lands another setStyle during that
 * await window (rapid provider switching, or the auto day/dusk/night palette
 * flipping mid-churn), the resumed callback runs addSource/addLayer against the
 * freshly-swapped style whose _loaded is momentarily false -> _checkLoaded throws
 * and the chart blanks. If it resumes a touch later it instead collides with the
 * new generation's freshly-added sources -> addSource throws `Source "…" already
 * exists.`. Same fault either way: stale-generation work mutating the wrong style.
 *
 * The cure is a monotonic generation counter, bumped on every setStyle. Async
 * re-apply captures the generation it belongs to and, before each batch of
 * mutations (and after every await), asks shouldApplyStyleWork(): am I still the
 * live generation AND is the style loaded? If a newer setStyle has superseded it,
 * the stale callback bails — the newer style runs (or already ran) its own
 * re-apply. This makes the sequencing correct instead of merely swallowing the
 * throw, and it lets a superseded styledata listener unregister itself instead of
 * firing against the new style.
 */

/** The next generation after `n`. Monotonic; call once per setStyle. */
export function nextEpoch(n: number): number {
  return n + 1;
}

export interface StyleWorkGate {
  /** True once the (current) style has parsed and is safe to mutate. */
  styleReady: boolean;
  /** The generation the pending work was scheduled under. */
  capturedEpoch: number;
  /** The live generation now (the latest setStyle wins). */
  currentEpoch: number;
}

/**
 * May this pending re-apply mutate the map right now? Only when the style is
 * loaded AND no newer setStyle has superseded the generation the work belongs to.
 * A superseded generation returns false so the stale callback bails out.
 */
export function shouldApplyStyleWork(gate: StyleWorkGate): boolean {
  return gate.styleReady && gate.capturedEpoch === gate.currentEpoch;
}
